/**
 * FORME PARALLÈLE de la compréhension de l'écrit en 4e, texte littéraire (11/10/2026) : « Le Copiste
 * d'Ombreval », écrit pour TanoEd à la manière d'un roman du XIXe siècle, et ses dix questions.
 *
 * Pas d'outil de positionnement du ministère en 4e : on reprend le texte de la variante de Petit Chose
 * (2026/francais/4e/petit_chose.js), corrigé d'après l'audit externe du 11/10 (« Le cas de Petit Chose » :
 * il suivait scène par scène l'extrait de Daudet lu en septembre, et l'élève en reconnaissait la trame).
 * Le cadre reste (Ombreval, la bibliothèque dans une ancienne abbaye, le jeune copiste, le bibliothécaire,
 * l'archiviste Brochard et sa tabatière) ; l'ordre des évènements et la fonction des scènes changent :
 *   - septembre : arrivée le soir, sous la pluie, en diligence ; le portier à lanterne le prend pour un
 *     écolier ; les longs couloirs ; le chef écrit, puis le trouve trop jeune ; la lettre de recommandation
 *     le sauve ; le second personnage sourit, mais son objet (les clés) « parle » avec mépris ; le nom du
 *     narrateur n'apparait qu'à la dernière réplique ; le règlement remis à la porte, qui se referme ;
 *   - ici : le récit commence au travail, le premier matin, le nom du narrateur sur la première page ; le
 *     bibliothécaire lui confie une copie que le tribunal attend (un procès pour un pré) ; l'archiviste,
 *     bourru, ne lui a dit que trois mots ; le copiste va trop vite pour briller ; l'archiviste découvre la
 *     ligne sautée ; la tentation de cacher la faute ; la nuit de travail à deux ; la tabatière sans tabac
 *     et le souvenir de l'archiviste (sa propre première faute) ; l'aveu au bibliothécaire ; la tabatière
 *     posée, ouverte, sur la table du copiste. Ni arrivée, ni portier, ni lettre, ni doute sur l'âge ;
 *     l'objet ne « parle » plus ; l'archiviste, bourru mais bienveillant, est l'inverse du surveillant de
 *     Daudet, souriant mais menaçant ; la relation entre les deux personnages évolue.
 *
 * Six pages du livre, au format de petit_chose.js (title, surtitre, content en texte brut, une ligne vide
 * entre les paragraphes), environ 1 000 mots, orthographe rectifiée, typographie de fabrique.js (typo). La
 * mention « Texte écrit pour TanoEd… » est la source de la page 6 : le livre du quiz (LivreImmersif)
 * l'affiche sous le texte, à droite, comme l'auteur d'un texte littéraire.
 *
 * Dix questions, réparties comme les items de septembre de ce texte : 3 explicite, 4 inference,
 * 3 sens_global (competences.js), avec les mêmes formats par famille (septembre : un tableau parmi les
 * explicites, Q03, un parmi les inférences, Q07 ; ici Q05 et Q07). Aucune ne reprend un item de septembre
 * (le titre, la région, le lieu en vrai ou faux, qui est décrit, l'émotion tirée d'une attitude, le motif de
 * l'embauche, la lettre en vrai ou faux, imaginaire ou réalité, l'atmosphère, le nom du narrateur) : ni la
 * tâche, ni la clé, ni la grille. QCM à quatre choix dans l'ordre fixé ici, la bonne réponse à des places
 * variées ; deux tableaux de quatre lignes. Chaque question dit en commentaire ce qu'elle mesure, son appui
 * (la citation) et la raison de chaque mauvais choix.
 * Relecture externe à l'aveugle du 11/10 : dix clés retrouvées sur dix, aucune réponse défendable de plus ;
 * corrigés ensuite : la phrase de la veuve Hubert (page 1 : on la lisait veuve d'un meunier de 1741), les
 * questions qui se devinaient par les autres (Q04, Q07, Q09, Q10), des mauvais choix sans effet (Q03, Q06),
 * Q05 en tableau.
 * Second chemin (REGLES_EQUIPE § 3) : chaque citation et chaque appui sont relus dans la page qu'ils
 * citent ; un texte retouché qui ne porte plus sa question lève une erreur au lieu de partir. Une personne
 * nommée n'est jamais reprise par « il » ou « elle » dans une question (gardé, comme le tiret cadratin).
 * Mode auto, À ARBITRER : le texte et les dix questions (relecture du prof à faire).
 */
import { typo, qcm as qcmForme, tableau as tableauForme, question as questionForme } from './fabrique';

/** Les paragraphes d'une page, séparés par une ligne vide, avec la typographie des formes parallèles. */
const paragraphes = (...p) => typo(p.join('\n\n'));

const MENTION = 'Texte écrit pour TanoEd, à la manière d’un roman du XIXe siècle.';

// ─── Le texte ───

export const SUPPORT_COPISTE = {
    id: 'forme-4e-le-copiste-d-ombreval',
    titre: "Le Copiste d'Ombreval",
    pages: [
        {
            page: 1,
            surtitre: 'Texte littéraire',
            title: 'Le Copiste d’Ombreval',
            content: paragraphes(
                'Le premier matin, à sept heures sonnantes, j’étais assis à ma table, les manches retroussées, devant un encrier plein et une pile de papier blanc. Sur le pupitre, une petite carte toute neuve portait mon nom : Lucien Vasseur, copiste. Je la relus dix fois, comme si c’était un titre de noblesse.',
                'La bibliothèque d’Ombreval occupait une ancienne abbaye, et la salle des copistes, son vieux réfectoire. Les moines y avaient pris leurs repas en silence pendant des siècles ; depuis qu’on y rangeait les archives de la ville, le silence n’en était jamais sorti.',
                'Le bibliothécaire entra, un gros registre relié de cuir sous le bras, et le posa devant moi avec précaution, comme on pose un enfant endormi.',
                '– Monsieur Vasseur, me dit-il, on m’assure que vous avez la plus belle écriture du lycée de Reims. Nous allons voir cela.',
                'Il ouvrit le registre à une page marquée d’un ruban rouge. C’était l’acte par lequel, en 1741, les moines avaient vendu le moulin des Aulnes à un meunier nommé Nicolas Hubert. Le moulin n’avait jamais quitté la famille : la veuve Hubert le faisait encore tourner avec ses trois fils. Elle était en procès contre un riche voisin, qui prétendait que le pré du moulin lui appartenait. Le tribunal réclamait une copie exacte de l’acte avant le lendemain midi.',
            ),
        },
        {
            page: 2,
            title: '',
            content: paragraphes(
                '– Pas une faute, pas une rature, pas un mot ajouté, conclut le bibliothécaire. Un juge ne lit pas une copie retouchée.',
                'À l’autre bout de la salle, M. Brochard, l’archiviste, n’avait pas levé le nez de ses liasses. C’était un petit vieillard vouté, à la barbe grise et aux sourcils épais, qui passait ses journées à classer des papiers jaunis en marmonnant des dates. Depuis mon arrivée, la veille, il ne m’avait adressé que trois mots : « Pas de chandelle ! », parce que j’en avais allumé une pour y voir plus clair. Chaque fois que ma chaise craquait, il tirait de sa poche une tabatière d’argent, l’ouvrait, la refermait d’un coup sec, et je rougissais jusqu’aux oreilles, comme un enfant pris en faute. Je le craignais plus que le bibliothécaire lui-même.',
                'Je me mis à l’ouvrage avec ardeur. L’encre des moines avait pâli, mais leur écriture était régulière, et ma plume courait sur le papier comme si elle avait hâte d’arriver au bout. Je voulais avoir fini avant le soir, pour montrer au bibliothécaire, dès le premier jour, à quel copiste il avait affaire. À midi, je ne descendis pas manger. À cinq heures, je traçai la dernière ligne, et je contemplai mes quatre pages avec un orgueil que je ne cherchai pas à cacher.',
            ),
        },
        {
            page: 3,
            title: '',
            content: paragraphes(
                'Une ombre tomba sur ma table. M. Brochard se tenait derrière moi. Sans un mot, il prit mes feuilles, les approcha de ses yeux et les posa à côté du vieux registre. Son doigt suivit lentement les lignes des moines, puis les miennes, et s’arrêta net au milieu de la deuxième page.',
                '– La plume va plus vite que l’œil, jeune homme, dit-il.',
                'Je regardai, et je crus que le plancher s’ouvrait sous ma chaise. Dans l’acte, deux lignes de suite commençaient par le même mot : « avec la maison du meunier », puis « avec le pré qui le borde jusqu’à la rivière ». Trompé par ce mot, mon œil avait sauté la seconde ligne, et le pré avait disparu de ma copie.',
                'La bibliothèque fermait à six heures. Il fallait tout recommencer, et jamais je n’aurais fini à temps. Je pensai un instant à glisser la ligne oubliée dans la marge, en lettres minuscules, et à me taire ; puis j’imaginai la veuve Hubert, que je n’avais jamais vue, debout devant le juge avec ses trois fils, et j’eus honte de ma pensée.',
            ),
        },
        {
            page: 4,
            title: '',
            content: paragraphes(
                'M. Brochard ne me laissa pas le temps d’hésiter davantage. Il alla fermer la porte à double tour, revint avec deux chandelles, les alluma lui-même et s’assit en face de moi, le registre ouvert sur ses genoux.',
                '– Je lis, vous écrivez, dit-il. Et vous ne levez pas la tête avant le point final.',
                'Toute la nuit, sa voix enrouée me dicta l’acte, ligne après ligne. Quand un mot était difficile, il me le faisait épeler ; quand ma main tremblait, il se taisait et attendait. Vers minuit, il ouvrit sa tabatière et me la tendit : elle ne contenait pas une miette de tabac, mais des pastilles de réglisse, noires et luisantes comme des boutons de bottine.',
                '– Pour ne pas dormir, grogna-t-il.',
                'Sur le couvercle, deux lettres étaient gravées, qui n’étaient pas les siennes : J. M. Je n’osai rien demander.',
            ),
        },
        {
            page: 5,
            title: '',
            content: paragraphes(
                'Ce fut le vieil archiviste qui parla, vers trois heures, sans quitter le registre des yeux.',
                '– Jacques Morin. Le copiste qui m’a appris le métier, il y a quarante ans. Mon premier jour, j’ai oublié trois lignes d’un testament. Il a passé la nuit avec moi, à cette même table.',
                'Il n’en dit pas davantage, et je n’en demandai pas plus ; mais, à partir de ce moment, ma main cessa de trembler.',
                'À six heures du matin, quand un jour pâle se leva derrière les fenêtres étroites, j’écrivis le point final. M. Brochard relut les quatre pages, le doigt sur chaque ligne, souffla les chandelles et retourna à ses liasses, à l’autre bout de la salle, comme si de rien n’était.',
            ),
        },
        {
            page: 6,
            title: '',
            content: paragraphes(
                'À sept heures, le bibliothécaire trouva la copie sur son bureau. Il la lut, la compara au registre et hocha la tête.',
                '– Propre et exacte, monsieur Vasseur. Le tribunal l’aura avant midi.',
                'Je pris mon courage à deux mains et lui racontai tout : la ligne sautée, la nuit, l’aide de M. Brochard. Le bibliothécaire m’écouta sans m’interrompre, puis regarda longuement l’archiviste, qui s’était mis à épousseter un registre qui n’en avait nul besoin.',
                '– Une faute qu’on avoue et qu’on répare, dit-il enfin, voilà le vrai commencement du métier.',
                'Je regagnai ma table. Ma chaise craqua quand je m’assis, et j’entendis, à l’autre bout de la salle, la tabatière s’ouvrir ; mais, cette fois, le couvercle ne claqua pas. M. Brochard traversa la salle à petits pas, posa la tabatière ouverte au coin de ma table, et retourna à ses papiers sans dire un mot.',
            ),
            source: MENTION,
        },
    ],
};

// ─── Le second chemin : le texte tient ses règles, chaque question retrouve son appui ───

const TEXTE = SUPPORT_COPISTE.pages.map((p) => p.content).join('\n\n');

/** Un texte ou une question qui ne tient plus sa règle lève une erreur au lieu de partir. */
const garde = (condition, message) => {
    if (!condition) throw new Error(`Forme parallèle de 4e, Le Copiste d’Ombreval : ${message}`);
};

/** Le texte de la page n (1 à 6). */
const page = (n) => SUPPORT_COPISTE.pages[n - 1]?.content || '';

/** Un passage tel qu'il est écrit à la page qu'on cite (typographie comprise). */
const cite = (n, passage) => {
    garde(page(n).includes(typo(passage)), `« ${passage} » n’est pas à la page ${n}`);
    return passage;
};

/** Combien de fois un mot apparait dans tout le texte. */
const occurrences = (mot) => TEXTE.split(mot).length - 1;

/** Les mots d'un texte, en minuscules (l'apostrophe et la ponctuation séparent). */
const mots = (s) => String(s).toLowerCase().split(/[^a-zà-öø-ÿœæ0-9]+/).filter(Boolean);

// Le texte : six pages, de 900 à 1 100 mots (sans la mention), le nom du narrateur dès la première page, la
// mention à la fin de la dernière, les répliques au tiret demi-cadratin et jamais de tiret cadratin ;
// l'orthographe rectifiée : plus d'accent circonflexe sur i et u, sauf dans les terminaisons verbales (fût,
// eût) et les mots qu'il distingue (sûr, dû, mûr, jeûne) ; évènement
const NOMBRE_DE_MOTS = TEXTE.replace(typo(MENTION), '').split(/\s+/).filter((m) => /[0-9A-Za-zÀ-ÿœ]/.test(m)).length;
const CIRCONFLEXES_PERMIS = new Set(['fût', 'eût', 'fît', 'sûr', 'sûre', 'dû', 'mûr', 'mûre', 'jeûne']);
garde(SUPPORT_COPISTE.pages.length === 6, 'six pages');
garde(NOMBRE_DE_MOTS >= 900 && NOMBRE_DE_MOTS <= 1100, `${NOMBRE_DE_MOTS} mots, au lieu de 900 à 1 100`);
garde(page(1).includes('Lucien Vasseur'), 'le nom du narrateur dès la première page');
garde(SUPPORT_COPISTE.pages[5].source === MENTION && !TEXTE.includes(MENTION), 'la mention en source de la dernière page');
garde(!TEXTE.includes('—'), 'un tiret cadratin dans le texte');
garde(TEXTE.split('\n\n').every((p) => !/^[-—]/.test(p)), 'une réplique sans tiret demi-cadratin');
garde(mots(TEXTE).every((m) => !/[îû]/.test(m) || CIRCONFLEXES_PERMIS.has(m)) && !/événement/i.test(TEXTE),
    'un mot en orthographe traditionnelle');

// ─── Les questions ───

const TEXTE_CLE = 'le_copiste_d_ombreval';
const qcm = (enonce, choix, bonne) => qcmForme(4, SUPPORT_COPISTE, enonce, choix, bonne);
const tableau = (enonce, colonnes, lignes, bonnes) => tableauForme(4, SUPPORT_COPISTE, enonce, colonnes, lignes, bonnes);
const question = (numero, competence, payload) => questionForme({
    niveau: 4, texte: TEXTE_CLE, numero, questionNum: `Q${String(numero).padStart(2, '0')}`, competence, payload,
});

/**
 * Q01 · explicite. Retrouver une information explicite, immédiatement repérable, au début du texte : le
 *   travail que le bibliothécaire confie au copiste.
 * Appui : page 1, « C’était l’acte par lequel, en 1741, les moines avaient vendu le moulin des Aulnes à un
 *   meunier nommé Nicolas Hubert. » et « le tribunal réclamait une copie exacte de l’acte » ; la bonne
 *   réponse reformule (vendu → vente).
 * Bonne réponse : C, « l’acte de vente d’un moulin. »
 * Mauvais choix :
 *   A « le testament d’un meunier. » : deux souvenirs mêlés, le meunier de la page 1 et le testament de la
 *     page 5 (la première faute de M. Brochard, quarante ans plus tôt) ;
 *   B « la liste des livres de l’abbaye. » : le travail qu’on imagine dans une bibliothèque, à la place de
 *     celui que le texte décrit ; aucune liste n’y est recopiée ;
 *   D « une lettre destinée au tribunal. » : le tribunal attend bien la copie, mais le copiste ne lui écrit
 *     pas : il recopie un acte.
 */
const q01 = () => {
    cite(1, 'C’était l’acte par lequel, en 1741, les moines avaient vendu le moulin des Aulnes à un meunier nommé Nicolas Hubert.');
    cite(1, 'Le tribunal réclamait une copie exacte de l’acte avant le lendemain midi');
    cite(5, 'j’ai oublié trois lignes d’un testament');
    garde(occurrences('testament') === 1 && !TEXTE.includes('liste'), 'Q01, un testament ou une liste de plus dans le texte');
    return question(1, 'explicite', qcm('Le bibliothécaire demande à Lucien Vasseur de recopier…', [
        'le testament d’un meunier.',
        'la liste des livres de l’abbaye.',
        'l’acte de vente d’un moulin.',
        'une lettre destinée au tribunal.',
    ], 'l’acte de vente d’un moulin.'));
};

/**
 * Q02 · explicite. Retrouver une information explicite au milieu du texte, sous une autre forme : la faute
 *   de la première copie.
 * Appui : page 3, « Trompé par ce mot, mon œil avait sauté la seconde ligne, et le pré avait disparu de ma
 *   copie. » ; page 6, « la ligne sautée ».
 * Bonne réponse : A, « sauté une ligne entière. »
 * Mauvais choix : les trois autres fautes ordinaires d’un copiste, chacune accrochée à un détail du texte,
 *   toutes démenties par la page 3 :
 *   B « mal recopié le nom du meunier. » : le seul nom de l’acte, Nicolas Hubert (page 1) ;
 *   C « écrit une mauvaise date. » : la seule date du texte, 1741 (page 1) ;
 *   D « ajouté un mot qui n’était pas dans le registre. » : l’interdit du bibliothécaire (« pas un mot
 *     ajouté », page 2) pris pour la faute commise ; c’est l’inverse, un oubli.
 */
const q02 = () => {
    cite(3, 'Trompé par ce mot, mon œil avait sauté la seconde ligne, et le pré avait disparu de ma copie.');
    cite(6, 'la ligne sautée');
    cite(1, 'un meunier nommé Nicolas Hubert');
    cite(2, 'pas un mot ajouté');
    garde((TEXTE.match(/\d{4}/g) || []).length === 1, 'Q02, une seule date dans le texte (1741)');
    return question(2, 'explicite', qcm('Dans sa première copie, Lucien Vasseur a…', [
        'sauté une ligne entière.',
        'mal recopié le nom du meunier.',
        'écrit une mauvaise date.',
        'ajouté un mot qui n’était pas dans le registre.',
    ], 'sauté une ligne entière.'));
};

/**
 * Q03 · inference. Comprendre le sens d’une phrase citée, en la reliant à ce qui la précède et à ce qui la
 *   suit.
 * Appui : page 3, « – La plume va plus vite que l’œil, jeune homme, dit-il. » ; avant, page 2, « ma plume
 *   courait sur le papier comme si elle avait hâte d’arriver au bout », « Je voulais avoir fini avant le
 *   soir » ; après, page 3, « Trompé par ce mot, mon œil avait sauté la seconde ligne ».
 * Bonne réponse : B, « a copié trop vite, sans assez regarder le registre. »
 * Mauvais choix (relecture externe du 11/10 : la citation seule ne doit pas suffire, A demande le texte) :
 *   A « a écrit si vite que son écriture est devenue difficile à lire. » : la hâte prise pour la cause d’une
 *     écriture illisible ; la faute est une ligne sautée par l’œil (page 3), la copie reste propre (au passé
 *     composé comme la clé : seconde relecture du 11/10) ;
 *   C « a besoin de lunettes pour lire le vieux registre. » : « l’œil » pris au pied de la lettre, avec
 *     « L’encre des moines avait pâli » (page 2) ; la faute vient de la hâte, pas de la vue ;
 *   D « devrait changer de plume. » : « la plume » prise au pied de la lettre : l’outil accusé à la place de
 *     celui qui écrit.
 */
const CITATION_Q03 = 'La plume va plus vite que l’œil, jeune homme';
const q03 = () => {
    cite(3, `– ${CITATION_Q03}, dit-il.`);
    cite(2, 'ma plume courait sur le papier comme si elle avait hâte d’arriver au bout');
    cite(2, 'Je voulais avoir fini avant le soir');
    cite(2, 'L’encre des moines avait pâli');
    cite(3, 'Trompé par ce mot, mon œil avait sauté la seconde ligne');
    garde(!TEXTE.includes('lunettes') && !/difficile à lire|illisible/.test(TEXTE), 'Q03, des lunettes ou une écriture illisible dans le texte');
    return question(3, 'inference', qcm(`« ${CITATION_Q03}. » (page 3) Par cette phrase, M. Brochard veut dire que Lucien Vasseur…`, [
        'a écrit si vite que son écriture est devenue difficile à lire.',
        'a copié trop vite, sans assez regarder le registre.',
        'a besoin de lunettes pour lire le vieux registre.',
        'devrait changer de plume.',
    ], 'a copié trop vite, sans assez regarder le registre.'));
};

/**
 * Q04 · inference. Mettre en relation deux informations éloignées : l’enjeu du procès (page 1) et la ligne
 *   oubliée (page 3). Le texte ne dit nulle part ce que la première copie aurait coûté (gardé : aucun
 *   « perdre » dans le texte).
 * Appui : page 1, « était en procès contre un riche voisin, qui prétendait que le pré du moulin lui
 *   appartenait » ; page 3, « le pré avait disparu de ma copie ».
 * Bonne réponse : D, « la veuve Hubert aurait pu perdre son pré. »
 * Mauvais choix :
 *   A « le riche voisin aurait perdu son procès. » : les deux camps inversés ; sans la ligne du pré, la
 *     copie sert le voisin ;
 *   B « le juge aurait refusé une copie raturée. » : la règle de la page 2 (« Un juge ne lit pas une copie
 *     retouchée ») appliquée à une copie qui n’avait pas de rature : elle était propre, mais incomplète ;
 *   C « la veuve Hubert aurait pu perdre la maison du meunier. » : la ligne recopiée prise pour la ligne
 *     sautée ; les deux commencent par « avec » (page 3), et seul le pré est réclamé (page 1). Relecture
 *     externe du 11/10 : C oblige à savoir quelle ligne manquait, D n’est plus le seul « aurait pu ».
 */
const q04 = () => {
    cite(1, 'était en procès contre un riche voisin, qui prétendait que le pré du moulin lui appartenait');
    cite(3, '« avec la maison du meunier », puis « avec le pré qui le borde jusqu’à la rivière »');
    cite(3, 'le pré avait disparu de ma copie');
    cite(2, 'Un juge ne lit pas une copie retouchée.');
    garde(!/perd/.test(TEXTE), 'Q04, le texte dit ce que la première copie aurait coûté : ce n’est plus une inférence');
    return question(4, 'inference', qcm('Si le tribunal avait reçu la première copie de Lucien Vasseur…', [
        'le riche voisin aurait perdu son procès.',
        'le juge aurait refusé une copie raturée.',
        'la veuve Hubert aurait pu perdre la maison du meunier.',
        'la veuve Hubert aurait pu perdre son pré.',
    ], 'la veuve Hubert aurait pu perdre son pré.'));
};

/**
 * Q05 · explicite. Retrouver des informations explicites sur deux pages éloignées, et les reconnaitre
 *   reformulées : quatre affirmations sur la tabatière, vraies ou fausses. Un tableau, comme l’un des
 *   explicites de septembre (Q03, le collège en Vrai / Faux) : mêmes formats par famille, pour que les taux
 *   se comparent (relecture externe du 11/10) ; un autre objet, d’autres lignes.
 * Lignes, réponse et appui :
 *   « La tabatière est en argent. » → Vrai : « une tabatière d’argent » (page 2) ;
 *   « La tabatière contient du tabac à priser. » → Faux : « elle ne contenait pas une miette de tabac, mais
 *     des pastilles de réglisse » (page 4) ; piège : ce qu’on attend d’une tabatière, et le mot « tabac » ;
 *   « Les lettres gravées sur le couvercle sont celles de M. Brochard. » → Faux : « deux lettres étaient
 *     gravées, qui n’étaient pas les siennes » (page 4) ; piège : on grave d’ordinaire ses initiales ;
 *   « M. Brochard partage le contenu de la tabatière avec Lucien Vasseur. » → Vrai : « il ouvrit sa tabatière
 *     et me la tendit » (page 4) ; le nom plutôt que « le copiste », qui désigne aussi Jacques Morin (page 5).
 */
const q05 = () => {
    cite(2, 'une tabatière d’argent');
    cite(4, 'elle ne contenait pas une miette de tabac, mais des pastilles de réglisse');
    cite(4, 'deux lettres étaient gravées, qui n’étaient pas les siennes');
    cite(4, 'il ouvrit sa tabatière et me la tendit');
    garde(occurrences('tabac') === 1, 'Q05, du tabac hors de la négation');
    return question(5, 'explicite', tableau('D’après les pages 2 et 4, indiquer si chaque affirmation est vraie ou fausse. Cocher une réponse par ligne.',
        ['Vrai', 'Faux'],
        [
            'La tabatière est en argent.',
            'La tabatière contient du tabac à priser.',
            'Les lettres gravées sur le couvercle sont celles de M. Brochard.',
            'M. Brochard partage le contenu de la tabatière avec Lucien Vasseur.',
        ],
        [0, 1, 1, 0]));
};

/**
 * Q06 · inference. Établir une relation de cause que le texte n’écrit pas : le souvenir que raconte
 *   M. Brochard explique son aide.
 * Appui : page 5, « Mon premier jour, j’ai oublié trois lignes d’un testament. Il a passé la nuit avec moi,
 *   à cette même table. » : M. Brochard fait pour le jeune copiste ce que Jacques Morin a fait pour lui.
 * Bonne réponse : C, « le souvenir de sa propre première faute. »
 * Mauvais choix :
 *   A « l’espoir d’une récompense de la veuve Hubert. » : un motif inventé ; M. Brochard ne parle jamais
 *     de la veuve Hubert, et aucune récompense n’est promise ;
 *   B « la crainte d’être blâmé si la copie arrivait en retard. » : l’échéance du tribunal (page 1) prise
 *     pour le motif ; M. Brochard n’est pas chargé de la copie, et le bibliothécaire ne sait rien de la nuit
 *     avant que le copiste la lui raconte (« lui racontai tout », page 6). Relecture externe du 11/10 : le
 *     choix « un ordre reçu du bibliothécaire » tombait par la ligne 3 de Q07 ;
 *   D « une vieille amitié avec la famille Vasseur. » : M. Brochard connait le copiste depuis la veille
 *     (« Depuis mon arrivée, la veille, il ne m’avait adressé que trois mots », page 2).
 */
const q06 = () => {
    cite(5, 'Mon premier jour, j’ai oublié trois lignes d’un testament. Il a passé la nuit avec moi, à cette même table.');
    cite(6, 'lui racontai tout');
    cite(1, 'Le tribunal réclamait une copie exacte de l’acte avant le lendemain midi');
    cite(2, 'Depuis mon arrivée, la veille, il ne m’avait adressé que trois mots');
    garde(!/récompense|amitié|blâm/.test(TEXTE), 'Q06, un mauvais choix appuyé par le texte');
    return question(6, 'inference', qcm('Ce qui pousse M. Brochard à aider Lucien Vasseur, c’est sans doute…', [
        'l’espoir d’une récompense de la veuve Hubert.',
        'la crainte d’être blâmé si la copie arrivait en retard.',
        'le souvenir de sa propre première faute.',
        'une vieille amitié avec la famille Vasseur.',
    ], 'le souvenir de sa propre première faute.'));
};

/**
 * Q07 · inference. Caractériser les personnages par ce qu’ils font : rattacher quatre comportements, dont
 *   aucun n’est nommé tel quel dans le texte, au personnage qui les a (tableau de quatre lignes).
 * Lignes, bonne colonne et appui :
 *   « fait mine d’être occupé sous le regard du bibliothécaire. » → M. Brochard : « puis regarda longuement
 *     l’archiviste, qui s’était mis à épousseter un registre qui n’en avait nul besoin » (page 6) ; il faut
 *     reconnaitre M. Brochard sous « l’archiviste » (« M. Brochard, l’archiviste », page 2) ;
 *   « interdit les chandelles, puis en allume deux. » → M. Brochard : « Pas de chandelle ! » (page 2), puis
 *     « revint avec deux chandelles, les alluma lui-même » (page 4) ; il faut relier deux passages, et le
 *     copiste, qui en a allumé une (page 2), est le piège ;
 *   « approuve la copie sans savoir que la première était fausse. » → le bibliothécaire : « Propre et exacte,
 *     monsieur Vasseur » (page 6), dit avant l’aveu (« lui racontai tout ») ;
 *   « est tenté un moment de cacher une faute. » → Lucien Vasseur : « Je pensai un instant à glisser la
 *     ligne oubliée dans la marge, en lettres minuscules, et à me taire » (page 3).
 * Relecture externe du 11/10 : la ligne « veut impressionner le bibliothécaire » (Lucien Vasseur) se cochait
 *   par simple bon sens ; la ligne des chandelles la remplace.
 * Mauvaises cases : M. Brochard pour la tentation de cacher (c’est son aide qu’il tait, pas une faute) ;
 *   Lucien Vasseur pour « fait mine d’être occupé » (pendant ce temps, le copiste parle) et pour les
 *   chandelles (il en allume une, il n’interdit rien) ; M. Brochard pour l’approbation (il sait tout de la
 *   nuit, et ne dit rien).
 */
const q07 = () => {
    cite(2, 'M. Brochard, l’archiviste');
    cite(6, 'puis regarda longuement l’archiviste, qui s’était mis à épousseter un registre qui n’en avait nul besoin');
    cite(2, '« Pas de chandelle ! », parce que j’en avais allumé une pour y voir plus clair');
    cite(4, 'revint avec deux chandelles, les alluma lui-même');
    cite(3, 'Je pensai un instant à glisser la ligne oubliée dans la marge, en lettres minuscules, et à me taire');
    const eloge = page(6).indexOf(typo('– Propre et exacte, monsieur Vasseur.'));
    garde(eloge >= 0 && eloge < page(6).indexOf('lui racontai tout'), 'Q07, l’approbation avant l’aveu');
    return question(7, 'inference', tableau('Pour chaque comportement, cocher le personnage concerné. Cocher une réponse par ligne.',
        ['Lucien Vasseur', 'M. Brochard', 'le bibliothécaire'],
        [
            'fait mine d’être occupé sous le regard du bibliothécaire.',
            'interdit les chandelles, puis en allume deux.',
            'approuve la copie sans savoir que la première était fausse.',
            'est tenté un moment de cacher une faute.',
        ],
        [1, 1, 2, 0]));
};

/**
 * Q08 · sens_global. Rendre compte de l’organisation du récit dans le temps : de quand à quand il se
 *   déroule, en rassemblant les repères de tout le texte.
 * Appuis : « Le premier matin, à sept heures sonnantes » (page 1), « À cinq heures, je traçai la dernière
 *   ligne » (page 2), « La bibliothèque fermait à six heures » (page 3), « Toute la nuit » (page 4), « À six
 *   heures du matin » (page 5), « À sept heures, le bibliothécaire trouva la copie » (page 6).
 * Bonne réponse : B, « du premier matin au lendemain matin. »
 * Mauvais choix :
 *   A « en une seule soirée. » : la nuit de travail, la scène la plus longue, prise pour tout le récit ;
 *   C « pendant toute une semaine. » : « Le premier matin » lu comme le début d’une suite de jours ;
 *   D « du matin au soir d’une seule et même journée. » : la lecture arrêtée à la fermeture de la
 *     bibliothèque (page 3).
 */
const q08 = () => {
    [[1, 'Le premier matin, à sept heures sonnantes'], [2, 'À cinq heures, je traçai la dernière ligne'],
        [3, 'La bibliothèque fermait à six heures'], [4, 'Toute la nuit'], [5, 'À six heures du matin'],
        [6, 'À sept heures, le bibliothécaire trouva la copie']].forEach(([n, passage]) => cite(n, passage));
    return question(8, 'sens_global', qcm('L’histoire racontée dans ce texte se déroule…', [
        'en une seule soirée.',
        'du premier matin au lendemain matin.',
        'pendant toute une semaine.',
        'du matin au soir d’une seule et même journée.',
    ], 'du premier matin au lendemain matin.'));
};

/**
 * Q09 · sens_global. Suivre la relation entre deux personnages sur tout le texte : son point de départ et
 *   son point d’arrivée.
 * Appuis : au début, « il ne m’avait adressé que trois mots » et « Je le craignais plus que le
 *   bibliothécaire lui-même » (page 2) ; à la fin, « mais, cette fois, le couvercle ne claqua pas » et
 *   « posa la tabatière ouverte au coin de ma table » (page 6).
 * Bonne réponse : D, « passe de la distance à la complicité. »
 * Mauvais choix :
 *   A « reste froide du début à la fin. » : M. Brochard reste bourru (« grogna-t-il », page 4 ; « sans dire
 *     un mot », page 6) ; l’élève ne voit pas que ses gestes ont changé ;
 *   B « passe de la confiance à la méfiance. » : la découverte de la faute (page 3) lue comme une brouille ;
 *     il n’y avait pas de confiance au départ ;
 *   C « se réchauffe un moment, puis redevient froide à la fin. » : la lecture arrêtée à la page 5
 *     (« retourna à ses liasses, à l’autre bout de la salle, comme si de rien n’était ») ; la page 6 la
 *     dément. Relecture externe du 11/10 : l’ancien choix (« complice dès leur première rencontre »)
 *     tombait sans lire, puisque l’énoncé de Q06 dit que M. Brochard aide le copiste.
 */
const q09 = () => {
    cite(2, 'il ne m’avait adressé que trois mots');
    cite(2, 'Je le craignais plus que le bibliothécaire lui-même.');
    cite(4, 'grogna-t-il');
    cite(5, 'retourna à ses liasses, à l’autre bout de la salle, comme si de rien n’était');
    cite(6, 'mais, cette fois, le couvercle ne claqua pas');
    cite(6, 'posa la tabatière ouverte au coin de ma table');
    return question(9, 'sens_global', qcm('Au fil du récit, la relation entre Lucien Vasseur et M. Brochard…', [
        'reste froide du début à la fin.',
        'passe de la confiance à la méfiance.',
        'se réchauffe un moment, puis redevient froide à la fin.',
        'passe de la distance à la complicité.',
    ], 'passe de la distance à la complicité.'));
};

/**
 * Q10 · sens_global. Identifier la visée du récit : ce qu’il montre, d’après la phrase qui le conclut et
 *   l’histoire des deux copistes.
 * Appuis : page 6, « – Une faute qu’on avoue et qu’on répare, dit-il enfin, voilà le vrai commencement du
 *   métier. » ; page 5, la première faute de M. Brochard ; page 3, la tentation de se taire, écartée
 *   (« et j’eus honte de ma pensée »).
 * Bonne réponse : A, « on apprend un métier en reconnaissant et en corrigeant ses erreurs. » ; elle dit la
 *   phrase finale avec d’autres mots (avouer → reconnaitre, réparer → corriger) : la reprise mot pour mot
 *   se trouverait par simple repérage.
 * Mauvais choix (relecture externe du 11/10 : la clé ne doit plus être la seule morale acceptable, face à
 *   deux absolus et une contre-morale ; trois leçons plausibles, chacune tirée d’une page du texte) :
 *   B « on apprend un métier en travaillant vite, pour montrer ce qu’on sait faire. » : l’ambition de la
 *     page 2 (« pour montrer au bibliothécaire, dès le premier jour, à quel copiste il avait affaire ») prise
 *     pour la leçon ; c’est la hâte qui a causé la faute. Même début que la clé : il faut lire la fin (seconde
 *     relecture du 11/10 : « mieux vaut cacher ses fautes » ne tentait personne) ;
 *   C « un bon copiste ne doit jamais se tromper. » : l’exigence de la page 2 (« Pas une faute ») prise pour
 *     la leçon ; le récit la dément : M. Brochard s’est trompé à ses débuts ;
 *   D « un bon copiste se reconnait d’abord à sa belle écriture. » : la réputation de la page 1 (« la plus
 *     belle écriture du lycée de Reims ») prise pour la leçon ; une copie bien écrite était fausse.
 */
const q10 = () => {
    cite(6, '– Une faute qu’on avoue et qu’on répare, dit-il enfin, voilà le vrai commencement du métier.');
    cite(5, 'Mon premier jour, j’ai oublié trois lignes d’un testament.');
    cite(3, 'et j’eus honte de ma pensée');
    cite(2, '– Pas une faute, pas une rature, pas un mot ajouté');
    cite(1, 'la plus belle écriture du lycée de Reims');
    return question(10, 'sens_global', qcm('Ce récit montre surtout que…', [
        'on apprend un métier en reconnaissant et en corrigeant ses erreurs.',
        'on apprend un métier en travaillant vite, pour montrer ce qu’on sait faire.',
        'un bon copiste ne doit jamais se tromper.',
        'un bon copiste se reconnait d’abord à sa belle écriture.',
    ], 'on apprend un métier en reconnaissant et en corrigeant ses erreurs.'));
};

export const QUESTIONS_COPISTE = [q01, q02, q03, q04, q05, q06, q07, q08, q09, q10].map((fabriquer) => fabriquer());

// Les questions : la répartition de septembre (3 / 4 / 3), quatre choix par QCM, quatre lignes au plus par
// tableau, jamais de tiret cadratin, jamais « il » ni « elle » (ni « lui ») à la place d'un nom
const REPARTITION = { explicite: 3, inference: 4, sens_global: 3 };
const PRONOMS = new Set(['il', 'elle', 'ils', 'elles', 'lui']);
const tableDe = ({ payload }) => (payload.elements_additionnels?.elements || []).find((e) => e.type === 'table') || null;
/** Ce qu'un élève lit d'une question : l'énoncé, les choix, les lignes et les colonnes d'un tableau. */
const textesVisibles = (q) => {
    const table = tableDe(q);
    return [
        q.payload.texte.replace(/<[^>]+>/g, ' '),
        ...(q.payload.contenu.choix || []),
        ...(table ? [...table.config.lignes, ...table.config.colonnes] : []),
    ];
};
QUESTIONS_COPISTE.forEach((q) => {
    const table = tableDe(q);
    const visibles = textesVisibles(q);
    garde(table ? table.config.lignes.length <= 4 : q.payload.contenu.choix.length === 4, `${q.questionNum}, quatre choix ou quatre lignes au plus`);
    garde(visibles.every((t) => !t.includes('—')), `${q.questionNum}, un tiret cadratin`);
    garde(visibles.every((t) => !mots(t).some((m) => PRONOMS.has(m))), `${q.questionNum}, un pronom à la place d’un nom`);
});
const parCompetence = QUESTIONS_COPISTE.reduce((m, q) => ({ ...m, [q.competence]: (m[q.competence] || 0) + 1 }), {});
garde(Object.entries(REPARTITION).every(([competence, n]) => parCompetence[competence] === n), 'la répartition 3 / 4 / 3');

/*
 * Aucune fuite : aucun choix ni aucune ligne ne donne la réponse d’une autre question. Les mots qui portent
 * une réponse ne sortent pas de leur question : le document recopié (Q01), la nature de la faute (Q02 :
 * ailleurs « première copie », « fausse », jamais « ligne » ni « oubli »), le contenu de la tabatière (Q05),
 * le souvenir de M. Brochard (Q06), les repères de temps (Q08), le début et la fin de la relation (Q09),
 * l’aveu (Q10 : ailleurs, seulement la tentation de cacher, Q07). La consigne d’un tableau (« une réponse
 * par ligne ») ne porte rien. Ce qui reste, comme dans les questionnaires du ministère : les énoncés
 * supposent connus les faits que tout lecteur retient (une première copie fausse, l’aide de M. Brochard),
 * sans donner aucune réponse.
 */
const PORTEURS = {
    Q01: /\b(acte|vente|moulin)/i,
    Q02: /\b(ligne|oubli|sauté)/i,
    Q05: /(réglisse|pastille|argent)/i,
    Q06: /(souvenir|première faute|débuts)/i,
    Q08: /\b(nuit|soir|matin|lendemain|semaine)/i,
    Q09: /(distan|froid|bourru|complic|confian|méfian)/i,
    Q10: /(reconnai|corrig|avou|aveu|répar)/i,
};
QUESTIONS_COPISTE.forEach((q) => {
    const lu = textesVisibles(q).join(' ').replace(/une réponse par ligne/g, '');
    Object.entries(PORTEURS).filter(([numero]) => numero !== q.questionNum)
        .forEach(([numero, motif]) => garde(!motif.test(lu), `${q.questionNum} laisse voir la réponse de ${numero}`));
});
